export const API_BASE = import.meta.env.VITE_API_URL || "https://api.echonour.com";

// Guest session ID for migrating transcriptions when they sign up
const GUEST_SESSION_KEY = "tempo_guest_session";

// Browser-compatible UUID v4 generator (replaces crypto.randomUUID for compatibility)
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export function getGuestSessionId(): string {
  let sessionId = localStorage.getItem(GUEST_SESSION_KEY);
  if (!sessionId) {
    sessionId = generateUUID();
    localStorage.setItem(GUEST_SESSION_KEY, sessionId);
  }
  return sessionId;
}

export function clearGuestSession(): void {
  localStorage.removeItem(GUEST_SESSION_KEY);
}

export class AuthRequiredError extends Error {
  constructor(message?: string) {
    super(message ?? "Authentication required");
    this.name = "AuthRequiredError";
  }
}

export class InsufficientBalanceError extends Error {
  minutes_remaining: number;
  minutes_needed: number;

  constructor(opts?: { message?: string; minutes_remaining?: number; minutes_needed?: number }) {
    super(opts?.message ?? "Insufficient transcription minutes");
    this.name = "InsufficientBalanceError";
    this.minutes_remaining = opts?.minutes_remaining ?? 0;
    this.minutes_needed = opts?.minutes_needed ?? 0;
  }
}

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = localStorage.getItem("tempo_token");

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) ?? {}),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  } else {
    // For guests, include session ID for library access
    headers["X-Guest-Session"] = getGuestSessionId();
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (res.status === 401) {
    localStorage.removeItem("tempo_token");
    throw new AuthRequiredError();
  }

  if (res.status === 402) {
    const body = await res.json().catch(() => ({}));
    const detail = typeof body.detail === "object" && body.detail !== null ? body.detail : body;
    const message = typeof body.detail === "string"
      ? body.detail
      : detail.message ?? body.message ?? "Insufficient transcription minutes";
    throw new InsufficientBalanceError({
      message,
      minutes_remaining: detail.minutes_remaining ?? 0,
      minutes_needed: detail.minutes_needed ?? 0,
    });
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const friendly: Record<string, string> = {
      video_unavailable:
        "YouTube prevents automated downloads of some videos. Try the podcast's RSS feed instead, or upload the audio file.",
      service_busy:
        "Our YouTube service is temporarily busy. Try again shortly or use an RSS feed for instant results.",
      bot_gate:
        "YouTube is currently blocking automated downloads of this video. Please try again later or try a different video.",
    };
    if (typeof body.error_type === "string" && friendly[body.error_type]) {
      throw new Error(friendly[body.error_type]);
    }
    throw new Error(body.detail ?? body.message ?? `Request failed (${res.status})`);
  }

  return res.json() as Promise<T>;
}
